import { Medication } from '@hooks/useMedicationsLoader';
import type { ComponentProps } from 'react';
import type { Alert } from 'antd';
import { ApptActionCodes } from 'vl-common/src/schemas/getMetaAppointments';
import { Moment } from 'moment';

export type RefClinType = {
  clin_user_guid?: string;
  hospital_id?: number;
  title?: string;
  first_name?: string;
  last_name?: string;
  cat_name?: string;
  cat_code?: string;
  spec_areas?: string | null;
  hospital_name?: string;
  postcode?: string;
};

export const ACTION_BKVCON_CODE = 'BKVCON';
export const ACTION_REFF2F_CODE = 'REFF2F';
export const ACTION_INVREQ_CODE = 'INVREQ';
export const ACTION_PRESCR_CODE = 'PRESCR';
export const ACTION_DISCHG_CODE = 'DISCHG';
export const ACTION_REF2AE_CODE = 'REF2AE';
export const ACTION_TRCARE_CODE = 'TRCARE';
export const ACTION_PTFACT_CODE = 'PTFACT';

export const ACTION_CODE_KEY = 'appt_action';
export const ACTION_SUBTYPE_KEY = 'action_subtype';
export const ACTION_AUTH_KEY = 'auth_boolean';
export const ACTION_AUTH_START_KEY = 'auth_start_date';
export const ACTION_AUTH_END_KEY = 'auth_end_date';
export const ACTION_REFER_SELF_KEY = 'refer_self';
export const ACTION_SPECIALITY_KEY = 'speciality';
export const ACTION_SUBSPECIALITY_KEY = 'subspeciality';
export const ACTION_REASON_KEY = 'referral_reason_code';
export const ACTION_COMMENT_KEY = 'actionTaskComment';
export const ACTION_OTHER_REASON_KEY = 'reason_for_referral';
export const ACTION_POSTCODE_KEY = 'postcode';
export const ACTION_URGENCY_KEY = 'urgency_code';
export const ACTION_REFCLI_KEY = 'refcli';
export const ACTION_CALL_TYPE_KEY = 'call_type';
export const ACTION_DISCHARGE_REASON_KEY = 'discharge_reason';

type ActionFieldsTypeMap = {
  speciality: string;
  subspeciality: string;
  referral_reason_code: string;
  reason_for_referral: string;
  ReasonForReferral: string;
  call_type: string;
  urgency_code: string;
  refcli: RefClinType;
  postcode: string;
  refer_self: 0 | 1;
  actionTaskComment: string;
  appt_action: any;
  action_subtype: string;
  auth_boolean: boolean;
  auth_start_date: Date;
  auth_end_date: Date;
  discharge_reason: string;
};

type GenericActionFields = {
  [P in keyof ActionFieldsTypeMap as `${P & string}_${ApptActionCodes}`]?: ActionFieldsTypeMap[P];
};

export interface SessionForm extends GenericActionFields {
  isActionsSaving: number;
  isSavingNotes: number;
  isSavingDataProtection: number;
  alert: { type: ComponentProps<typeof Alert>['type']; message: React.ReactNode } | null;
  IMPRO: string;
  HPC: string;
  PLAN: string;
  WD: string;
  'D-COM': string;
  INT: string;
  UPDATE: string;
  QUICK: string;
  RECOMMENDATIONS: string[];
  red_flag: boolean;
  complex_case: boolean;
  Priority: number;
  'Referral To Treatment': number;
  appointment_outcome: string;
  'Letter Template to use': string;
  letterReq: { letter_code: string; letter_template_type: string };
  AssociatedTasks: string;
  clinician_postcode: string;
  pifu_validity: string;
  pifu_self_book: string;
  pifu_outcome: string;
  pifu_eligibility: string;

  speciality?: string;
  subspeciality?: string;
  referral_reason_code?: string;
  ReasonForReferral: string;
  call_type?: string;
  clinician_type?: string;
  refer_self?: 0 | 1;
  SendGDPREmail: string;
  request_type: string;
  refetchInvestigationRequests: number;
  request: boolean;
  urgency_code?: string;
  cancellation_reason: string;
  refcli?: RefClinType;
  /** The medications currently selected for prescription */
  tableMedications?: Pick<
    Medication,
    | 'vtm_id'
    | 'vtm_name'
    | 'vmp_options'
    | 'vmp_name'
    | 'vmp_id'
    | 'current_vmp'
    | 'med_id'
    | 'bnf_url'
    | 'frequency'
    | 'duration'
  >[];
  /** A filtered subset of unselected medication displayed in the autocomplete */
  filteredMedications?: { value: string; vtm_id: string; vtm_name: string; bnf_url: string | null }[];
  prescribeMedication?: boolean;
  hasPrescriptionValueBeenUpdated?: boolean;
  icd10Value: string;
  postcode?: string;
  auth?: boolean;
  auth_BKVCON?: boolean;
  authDate?: Moment;
  authStartDate?: Moment;
  actionTaskComment?: string;
  f2fComplete?: boolean;
  f2fReferralPosted?: boolean;
  postedReferralStartDate?: Date;
  factsheets?: {
    factsheet_id: number;
    factsheet_name: string;
    factsheet_url: string;
  }[];
  appt_actions: {
    appt_action_code: ApptActionCodes;
  }[];
  /** When the action_subtype_code is DISCHARGE, a discharge_code is required */
  appt_action_DISCHG?:
    | { appt_action_code: 'DISCHG'; action_subtype_code: 'DISCHARGE'; discharge_code: string }
    | { appt_action_code: 'DISCHG'; action_subtype_code: string };
  appt_action_BKVCON?: {
    appt_action_code: 'BKVCON';
    refer_self?: 0 | 1;
    action_subtype_code?: string;
    action_spec_cat_code?: string;
    action_spec_area_code?: string;
    auth_granted?: 0 | 1;
    auth_start_date?: string;
    action_task_comment?: string;
    action_reason_code?: string;
    call_type?: 'AUDIO' | 'VIDEO';
  };
  generalAdvice?: {
    genadv_code: string;
  }[];
  investigations?: {
    service_type_code: string;
  }[];
  discharge_reason?: string;
  letterViewed: boolean;
  isLetterReadToBottom: boolean;
  isLetterAccuracyConfirmed: boolean;
  isDPACheck: boolean;
  videoCallDidStart: boolean;
  audioCallDidStart: boolean;
  videoCallIssues: ('seePatient' | 'patientSeeYou' | 'hearPatient' | 'patientHearYou')[];
  audioCallIssues: ('hearPatient' | 'patientHearYou')[];
  networkType: 'wifi' | 'mobile' | 'unknown';
  impact: 'none' | 'minor' | 'major' | 'failed';
  docmanDiscussed: boolean;
}
